import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import UserSidebar from "@/components/UserSidebar";
import { Package, CheckCircle, Truck, Clock, Store, ShipIcon, Coins, Download, CalendarClock, ShoppingCart } from "lucide-react";
import { fetchProductPage, convertToPoints, downloadDigitalProduct, type Product, type ProductStatusFilter } from "@/services/customerService";
import { useAuth } from "@/components/AuthContext";
import { toast } from "sonner";
import ResponsiveImage from "@/components/ResponsiveImage";

type Tab = 'pending' | 'downloaded' | 'shipping' | 'shipped' | 'converted';

const statusLabels: Record<string, string> = {
  preparing: '準備中',
  shipped: '発送済み',
  in_transit: '配送中',
  delivered: '配達完了',
  received: '受取確認済み',
  cancelled: 'キャンセル',
  returned: '返送'
};

// PT交換可能かどうかを判定（発送予定日を過ぎていたら不可）
const canConvertToPoints = (product: Product): boolean => {
  if (!product.allow_point_conversion || product.point_value <= 0) return false;
  if (product.auto_ship_deadline && new Date(product.auto_ship_deadline) < new Date()) return false;
  return true;
};

// PT交換期限切れかどうかを判定
const isConversionExpired = (product: Product): boolean => {
  if (!product.allow_point_conversion || product.point_value <= 0) return false;
  return !!(product.auto_ship_deadline && new Date(product.auto_ship_deadline) < new Date());
};

const KujiProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [convertingId, setConvertingId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [displayCount, setDisplayCount] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const latestRequestId = useRef(0);

  const activeTab = (searchParams.get('tab') as Tab) || 'pending';

  useEffect(() => {
    setDisplayCount(20);
    loadProducts(false, 1, false);
  }, [activeTab]);

  const loadProducts = async (silent = false, page = 1, append = false): Promise<boolean> => {
    const requestId = ++latestRequestId.current;
    try {
      // silent=true: 再取得時はローディング状態にしない（一瞬エラー表示が見えるのを防止）
      if (!silent) setLoading(true);
      if (append) setLoadingMore(true);
      const data = await fetchProductPage({ eventType: 'kuji', status: activeTab as ProductStatusFilter, page, perPage: 100 });
      if (requestId !== latestRequestId.current) return false;
      setProducts(previous => append ? [...previous, ...data.products] : data.products);
      setCurrentPage(data.pagination.page);
      setHasMore(data.pagination.has_more);
      return true;
    } catch (error) {
      if (requestId === latestRequestId.current) {
        if (!silent) toast.error('商品の取得に失敗しました');
        console.error(error);
      }
      return false;
    } finally {
      if (requestId === latestRequestId.current) {
        if (!silent) setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const filteredProducts = products.filter(p => {
    if (activeTab === 'pending') {
      return p.status === 'pending' && !p.downloaded;
    }
    if (activeTab === 'downloaded') {
      return p.status === 'pending' && p.downloaded;
    }
    return p.status === activeTab;
  });

  const productsByOrder = useMemo(() => {
    if (activeTab !== 'shipping' && activeTab !== 'shipped') return [];

    const relevantProducts = activeTab === 'shipped'
      ? products.filter(p => p.status === 'shipped' || (p.order_status === 'received'))
      : filteredProducts;

    const grouped = new Map<number, { order_id: number; site: Product['site']; order_status: string; products: Product[] }>();

    relevantProducts.forEach(product => {
      const orderId = product.order_id!;
      if (!grouped.has(orderId)) {
        grouped.set(orderId, { order_id: orderId, site: product.site, order_status: product.order_status || 'preparing', products: [] });
      }
      grouped.get(orderId)!.products.push(product);
    });

    return Array.from(grouped.values());
  }, [activeTab, filteredProducts, products]);

  type CheckoutGroup = { checkout_group_id: string; event_name: string; event_uid: string; quantity: number; is_bulk_purchase: boolean; drawn_at: string; products: Product[] };
  const groupedByCheckout = useMemo(() => {
    const groups: (Product | CheckoutGroup)[] = [];
    const grouped = new Map<string, Product[]>();
    const ungrouped: Product[] = [];

    filteredProducts.forEach(p => {
      if (p.checkout_group_id) {
        if (!grouped.has(p.checkout_group_id)) grouped.set(p.checkout_group_id, []);
        grouped.get(p.checkout_group_id)!.push(p);
      } else {
        ungrouped.push(p);
      }
    });

    grouped.forEach((items, gid) => {
      if (items.length >= 2) {
        const first = items[0];
        groups.push({
          checkout_group_id: gid,
          event_name: first.event_name || '',
          event_uid: first.event_uid || '',
          quantity: items.filter(i => !i.is_bulk_bonus).length,
          is_bulk_purchase: !!first.bulk_purchase_quantity,
          drawn_at: first.drawn_at,
          products: items
        });
      } else {
        ungrouped.push(...items);
      }
    });

    ungrouped.forEach(p => groups.push(p));

    groups.sort((a, b) => {
      const dateA = 'drawn_at' in a ? a.drawn_at : '';
      const dateB = 'drawn_at' in b ? b.drawn_at : '';
      return dateB.localeCompare(dateA);
    });

    return groups;
  }, [filteredProducts]);

  const handleTabChange = (tab: Tab) => {
    setSearchParams({ tab });
  };

  const handleLoadMore = async () => {
    const renderedCount = activeTab === 'shipping' || activeTab === 'shipped' ? productsByOrder.length : groupedByCheckout.length;
    if (displayCount < renderedCount) {
      setDisplayCount(previous => previous + 20);
    } else if (hasMore) {
      const loaded = await loadProducts(true, currentPage + 1, true);
      if (loaded) setDisplayCount(previous => previous + 20);
    }
  };

  const handleDownload = async (product: Product, skipConfirm: boolean = false) => {
    if (!skipConfirm && !product.downloaded && canConvertToPoints(product)) {
      const confirmMessage = `ダウンロードするとPT交換ができなくなります。よろしいですか？`;
      if (!confirm(confirmMessage)) return;
    }

    try {
      setDownloadingId(product.lottery_result_id);
      const result = await downloadDigitalProduct(product.lottery_result_id);
      // iPhone Safari は非同期後の window.open をポップアップブロックするため、
      // <a> タグを動的に生成して click() でダウンロードを開始する
      const link = document.createElement('a');
      link.href = result.download_url;
      link.download = '';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('ダウンロードを開始しました');
      await loadProducts(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'ダウンロードに失敗しました');
      console.error(error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleConvertSingle = async (product: Product) => {
    if (!product.allow_point_conversion || product.point_value <= 0) {
      toast.error('この商品はポイント交換できません');
      return;
    }

    let confirmMessage = `「${product.name}」を${product.point_value.toLocaleString()}PTに交換しますか？\n※この操作は取り消せません`;
    if (product.content_type === 'digital' && !product.downloaded) {
      confirmMessage = `PT交換するとダウンロードができなくなります。\n\n「${product.name}」を${product.point_value.toLocaleString()}PTに交換しますか？\n※この操作は取り消せません`;
    }
    if (!confirm(confirmMessage)) return;

    try {
      setConvertingId(product.lottery_result_id);
      const result = await convertToPoints([product.lottery_result_id]);
      toast.success(result.message);
      // ポイント残高を更新
      refreshUser();
      await loadProducts(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'ポイント交換に失敗しました');
      console.error(error);
    } finally {
      setConvertingId(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>獲得した賞品</title>
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div>
          <div className="max-w-screen-lg mx-auto px-4 pb-4 sm:pb-8 pt-20">
            <div className="flex gap-8">
              <div className="hidden lg:block">
                <UserSidebar />
              </div>

              <section className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                    <Package className="h-5 w-5 text-theme-button" />
                  </div>
                  <div>
                    <h1 className="text-xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">獲得した賞品</h1>
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">抽選で獲得した賞品を管理できます</p>
                  </div>
                </div>

          <div className="flex gap-2 mb-6 overflow-x-auto">
            <button onClick={() => handleTabChange('pending')} className={`px-4 py-2 [font-weight:var(--theme-font-weight-button)] text-sm rounded-theme border-theme transition-all whitespace-nowrap ${activeTab === 'pending' ? 'bg-theme-primary text-theme-button shadow-theme-sm' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              <Clock className="inline h-4 w-4 mr-1" />未選択
            </button>
            <button onClick={() => handleTabChange('shipping')} className={`px-4 py-2 [font-weight:var(--theme-font-weight-button)] text-sm rounded-theme border-theme transition-all whitespace-nowrap ${activeTab === 'shipping' ? 'bg-theme-primary text-theme-button shadow-theme-sm' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              <Package className="inline h-4 w-4 mr-1" />発送依頼中
            </button>
            <button onClick={() => handleTabChange('shipped')} className={`px-4 py-2 [font-weight:var(--theme-font-weight-button)] text-sm rounded-theme border-theme transition-all whitespace-nowrap ${activeTab === 'shipped' ? 'bg-theme-primary text-theme-button shadow-theme-sm' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              <CheckCircle className="inline h-4 w-4 mr-1" />発送済
            </button>
            <button onClick={() => handleTabChange('converted')} className={`px-4 py-2 [font-weight:var(--theme-font-weight-button)] text-sm rounded-theme border-theme transition-all whitespace-nowrap ${activeTab === 'converted' ? 'bg-theme-primary text-theme-button shadow-theme-sm' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              <Coins className="inline h-4 w-4 mr-1" />PT交換済
            </button>
            <button onClick={() => handleTabChange('downloaded')} className={`px-4 py-2 [font-weight:var(--theme-font-weight-button)] text-sm rounded-theme border-theme transition-all whitespace-nowrap ${activeTab === 'downloaded' ? 'bg-theme-primary text-theme-button shadow-theme-sm' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              <Download className="inline h-4 w-4 mr-1" />DL済み
            </button>
          </div>

          <div className="space-y-4 mb-20">
            {loading ? (
              <div className="text-center py-12"><p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">読み込み中...</p></div>
            ) : filteredProducts.length === 0 ? (
              <div className="bg-white border-4 border-dashed border-gray-300 rounded-theme-lg p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 border-2 border-gray-300 rounded-theme-lg flex items-center justify-center mx-auto mb-4"><Package className="h-8 w-8 text-gray-400" /></div>
                <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">商品がありません</p>
              </div>
            ) : activeTab === 'shipping' || activeTab === 'shipped' ? (
              productsByOrder.slice(0, displayCount).map(({ order_id, site, order_status, products }) => {
                const firstProduct = products[0];
                const trackingNumber = firstProduct.order_tracking_number;
                const statusLabel = statusLabels[order_status] || order_status;
                const getStatusDisplay = () => {
                  switch (order_status) {
                    case 'preparing': return { bgColor: 'bg-gray-50', badgeColor: 'bg-gray-100 text-gray-800', icon: <Clock className="h-3 w-3" />, message: '店舗が発送準備中です' };
                    case 'shipped': return { bgColor: 'bg-yellow-50', badgeColor: 'bg-yellow-100 text-yellow-800', icon: <ShipIcon className="h-3 w-3" />, message: trackingNumber ? '商品が発送されました' : '発送済み' };
                    case 'in_transit': return { bgColor: 'bg-blue-50', badgeColor: 'bg-blue-100 text-blue-800', icon: <Truck className="h-3 w-3" />, message: '配送業者が配送中です' };
                    case 'delivered': return { bgColor: 'bg-green-50', badgeColor: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-3 w-3" />, message: '配達完了しました。' };
                    case 'received': return { bgColor: 'bg-green-50', badgeColor: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-3 w-3" />, message: '受取確認済みです。ご利用ありがとうございました。' };
                    default: return { bgColor: 'bg-gray-50', badgeColor: 'bg-gray-100 text-gray-800', icon: null, message: '' };
                  }
                };

                const statusDisplay = getStatusDisplay();

                return (
                  <div key={order_id} className="bg-white border-theme rounded-theme-lg shadow-theme overflow-hidden">
                    <div className={`${statusDisplay.bgColor} px-4 py-3 border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-orange-400 border-theme rounded flex items-center justify-center"><Store className="h-3 w-3 text-black" /></div>
                          <h3 className="[font-weight:var(--theme-font-weight)] text-sm text-black">{site.name}</h3>
                          <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">({products.length}件)</span>
                        </div>
                        <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-theme border-theme [font-weight:var(--theme-font-weight-button)] ${statusDisplay.badgeColor}`}>{statusDisplay.icon}<span>{statusLabel}</span></div>
                      </div>
                      {statusDisplay.message && <div className="text-xs font-medium text-gray-700 mb-2">{statusDisplay.message}</div>}
                      {trackingNumber && <div className="text-xs text-gray-700 mt-1 bg-white border-theme px-2 py-1.5 rounded-theme font-medium"><span className="[font-weight:var(--theme-font-weight-button)]">追跡番号:</span> {trackingNumber}</div>}
                    </div>
                    <div className="divide-y divide-[var(--theme-border-color)]">
                      {products.map(product => (
                        <div key={product.lottery_result_id} className="p-4 bg-white">
                          <div className="flex gap-3">
                            <div className="w-16 bg-gray-100 rounded-theme overflow-hidden flex-shrink-0 border-theme" style={{ aspectRatio: '3/4' }}>
                              <ResponsiveImage src={product.image_urls[0]} variants={product.image_variant_urls} alt={product.name} displaySize="card" className="w-full h-full object-contain" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-1 line-clamp-2">{product.name}</h4>
                              <span className="inline-block px-2 py-0.5 bg-theme-primary border-theme text-theme-button rounded-theme text-xs [font-weight:var(--theme-font-weight-button)]">{product.rarity}</span>
                              {product.event_name && <Link to={`/kuji/${product.event_uid}`} className="text-xs font-medium text-blue-600 hover:underline block mt-1">{product.event_name}</Link>}
                              <p className="text-xs font-medium text-gray-600 mt-1">当選日時: {new Date(product.drawn_at).toLocaleString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            ) : activeTab === 'pending' || activeTab === 'downloaded' || activeTab === 'converted' ? (
              groupedByCheckout.slice(0, displayCount).map(item => {
                if ('checkout_group_id' in item && 'products' in item) {
                  const group = item as CheckoutGroup;
                  return (
                    <div key={group.checkout_group_id} className="bg-white border-theme rounded-theme-lg shadow-theme overflow-hidden">
                      <div className="bg-orange-50 px-4 py-3 border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-orange-400 border-theme rounded flex items-center justify-center"><ShoppingCart className="h-3 w-3 text-black" /></div>
                            <Link to={`/kuji/${group.event_uid}`} className="[font-weight:var(--theme-font-weight)] text-sm text-black hover:text-blue-600 hover:underline">{group.event_name}</Link>
                            <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">({group.products.length}件)</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {group.is_bulk_purchase && <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-800 border-theme rounded-theme [font-weight:var(--theme-font-weight-button)]">まとめ買い</span>}
                            <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">{new Date(group.drawn_at).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      </div>
                      <div className="divide-y divide-[var(--theme-border-color)]">
                        {group.products.map(product => (
                          <div key={product.lottery_result_id} className="p-4">
                            <div className="flex gap-3">
                              <div className="w-16 bg-gray-100 rounded-theme overflow-hidden flex-shrink-0 border-theme" style={{ aspectRatio: '3/4' }}>
                                <ResponsiveImage src={product.image_urls[0]} variants={product.image_variant_urls} alt={product.name} displaySize="card" className="w-full h-full object-contain" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-1 line-clamp-2">{product.name}</h4>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {product.is_bulk_bonus ? (
                                    <span className="px-1.5 py-0.5 bg-blue-100 border-theme text-blue-800 rounded text-xs [font-weight:var(--theme-font-weight-button)]">セット購入特典</span>
                                  ) : (
                                    <span className="px-1.5 py-0.5 bg-theme-primary border-theme text-theme-button rounded text-xs [font-weight:var(--theme-font-weight-button)]">{product.rarity}</span>
                                  )}
                                </div>
                                {activeTab === 'converted' && <div className="flex items-center gap-1 mt-1 text-xs text-green-700 [font-weight:var(--theme-font-weight-button)]"><Coins className="h-3 w-3" />{product.converted_point_value?.toLocaleString()} PT に交換済み</div>}
                                {activeTab === 'pending' && canConvertToPoints(product) && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <Coins className="h-3 w-3 text-yellow-600" />
                                    <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-yellow-600">
                                      {product.point_value.toLocaleString()} PT
                                      {product.auto_ship_deadline && <span className="text-gray-500">（交換期限: {new Date(product.auto_ship_deadline).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}まで）</span>}
                                    </span>
                                  </div>
                                )}
                                {activeTab === 'pending' && isConversionExpired(product) && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <Coins className="h-3 w-3 text-gray-400" />
                                    <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-400">
                                      {product.point_value.toLocaleString()} PT（交換期限切れ）
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {(activeTab === 'pending' || activeTab === 'downloaded') && (
                              <div className="flex gap-2 mt-3">
                                {product.content_type === 'digital' ? (
                                  <button onClick={() => handleDownload(product, activeTab === 'downloaded')} disabled={downloadingId === product.lottery_result_id} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-theme-primary text-theme-button border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50">
                                    <Download className="h-3 w-3" />{downloadingId === product.lottery_result_id ? '...' : (activeTab === 'downloaded' ? '再ダウンロード' : 'ダウンロード')}
                                  </button>
                                ) : product.auto_ship_deadline ? (
                                  <div className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-orange-50 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] text-orange-700">
                                    <CalendarClock className="h-3 w-3 flex-shrink-0" /><span>{new Date(product.auto_ship_deadline).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}以降順次発送</span>
                                  </div>
                                ) : (
                                  <div className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-50 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">
                                    <Truck className="h-3 w-3 flex-shrink-0" /><span>準備整い次第順次発送</span>
                                  </div>
                                )}
                                {activeTab === 'pending' && canConvertToPoints(product) && (
                                  <button onClick={() => handleConvertSingle(product)} disabled={convertingId === product.lottery_result_id} className="flex items-center justify-center gap-1 px-3 py-1.5 bg-green-400 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50">
                                    <Coins className="h-3 w-3" />{convertingId === product.lottery_result_id ? '...' : 'PT交換'}
                                  </button>
                                )}
                                {activeTab === 'pending' && isConversionExpired(product) && (
                                  <div className="flex items-center justify-center gap-1 px-3 py-1.5 bg-gray-300 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">
                                    <Coins className="h-3 w-3" />期限切れ
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                } else {
                  const product = item as Product;
                  return (activeTab === 'pending' || activeTab === 'downloaded') ? (
                    <div key={product.lottery_result_id} className="bg-white border-theme rounded-theme-lg overflow-hidden shadow-theme">
                      <div className="p-4">
                        <div className="flex gap-3">
                          <div className="w-16 bg-gray-100 rounded-theme overflow-hidden flex-shrink-0 border-theme" style={{ aspectRatio: '3/4' }}>
                            <ResponsiveImage src={product.image_urls[0]} variants={product.image_variant_urls} alt={product.name} displaySize="card" className="w-full h-full object-contain" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black line-clamp-2 mb-1">{product.name}</h3>
                            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                              <span className="px-1.5 py-0.5 bg-theme-primary border-theme text-theme-button rounded text-xs [font-weight:var(--theme-font-weight-button)]">{product.rarity}</span>
                              <span className="px-1.5 py-0.5 border-theme text-black rounded text-xs [font-weight:var(--theme-font-weight-button)] bg-orange-400">スクラッチ</span>
                            </div>
                            {product.event_name && <Link to={`/kuji/${product.event_uid}`} className="text-xs font-medium text-blue-600 hover:underline block mb-0.5">{product.event_name}</Link>}
                            <p className="text-xs font-medium text-gray-600">当選日時: {new Date(product.drawn_at).toLocaleString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            {canConvertToPoints(product) && (
                              <div className="flex items-center gap-1 mt-1">
                                <Coins className="h-3 w-3 text-yellow-600" />
                                <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-yellow-600">
                                  {product.point_value.toLocaleString()} PT
                                  {product.auto_ship_deadline && <span className="text-gray-500">（交換期限: {new Date(product.auto_ship_deadline).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}まで）</span>}
                                </span>
                              </div>
                            )}
                            {isConversionExpired(product) && (
                              <div className="flex items-center gap-1 mt-1">
                                <Coins className="h-3 w-3 text-gray-400" />
                                <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-400">
                                  {product.point_value.toLocaleString()} PT（交換期限切れ）
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          {product.content_type === 'digital' ? (
                            <button onClick={() => handleDownload(product, activeTab === 'downloaded')} disabled={downloadingId === product.lottery_result_id} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-theme-primary text-theme-button border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50">
                              <Download className="h-3 w-3" />{downloadingId === product.lottery_result_id ? '...' : (activeTab === 'downloaded' ? '再ダウンロード' : 'ダウンロード')}
                            </button>
                          ) : product.auto_ship_deadline ? (
                            <div className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-orange-50 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] text-orange-700">
                              <CalendarClock className="h-3 w-3 flex-shrink-0" /><span>{new Date(product.auto_ship_deadline).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}以降順次発送</span>
                            </div>
                          ) : (
                            <div className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-50 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600">
                              <Truck className="h-3 w-3 flex-shrink-0" /><span>準備整い次第順次発送</span>
                            </div>
                          )}
                          {activeTab === 'pending' && canConvertToPoints(product) && (
                            <button onClick={() => handleConvertSingle(product)} disabled={convertingId === product.lottery_result_id} className="flex items-center justify-center gap-1 px-3 py-1.5 bg-green-400 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all disabled:opacity-50">
                              <Coins className="h-3 w-3" />{convertingId === product.lottery_result_id ? '...' : 'PT交換'}
                            </button>
                          )}
                          {activeTab === 'pending' && isConversionExpired(product) && (
                            <div className="flex items-center justify-center gap-1 px-3 py-1.5 bg-gray-300 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">
                              <Coins className="h-3 w-3" />期限切れ
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={product.lottery_result_id} className="bg-white border-theme rounded-theme-lg overflow-hidden shadow-theme">
                      <div className="p-4">
                        <div className="flex gap-4">
                          <div className="w-24 bg-gray-100 rounded-theme overflow-hidden flex-shrink-0 border-theme" style={{ aspectRatio: '3/4' }}>
                            <ResponsiveImage src={product.image_urls[0]} variants={product.image_variant_urls} alt={product.name} displaySize="card" className="w-full h-full object-contain" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base [font-weight:var(--theme-font-weight-button)] text-black line-clamp-2 mb-2">{product.name}</h3>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className="px-2 py-1 bg-theme-primary border-theme text-theme-button rounded-theme text-xs [font-weight:var(--theme-font-weight-button)]">{product.rarity}</span>
                              <span className="px-2 py-1 border-theme text-black rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] bg-orange-400">スクラッチ</span>
                            </div>
                            {product.event_name && <Link to={`/kuji/${product.event_uid}`} className="text-xs font-medium text-blue-600 hover:underline block mb-2">{product.event_name}</Link>}
                            <div className="flex items-center gap-2 mt-2 bg-green-50 border-2 border-green-200 rounded-theme px-3 py-2">
                              <Coins className="h-4 w-4 text-green-600" />
                              <div>
                                <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-green-700">{product.converted_point_value?.toLocaleString()} PT に交換済み</p>
                                <p className="text-xs text-green-600">{product.point_converted_at && new Date(product.point_converted_at).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
              })
            ) : null}

            {!loading && (() => {
              const total = (activeTab === 'shipping' || activeTab === 'shipped') ? productsByOrder.length : (activeTab === 'pending' || activeTab === 'downloaded' || activeTab === 'converted') ? groupedByCheckout.length : filteredProducts.length;
              return total > displayCount || hasMore ? (
                <button onClick={handleLoadMore} disabled={loadingMore} className="w-full py-3 bg-white border-theme rounded-theme text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50">{loadingMore ? '読み込み中...' : 'もっと見る'}</button>
              ) : null;
            })()}
          </div>

              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default KujiProductsPage;
